// @ts-nocheck
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { isToday, isThisWeek, isPast, format } from "date-fns";
import { CalendarDays, CalendarClock, ShieldCheck, Award, ArrowRight } from "lucide-react";
import { fetchCalendarEvents, type CalendarEvent } from "@/lib/api/calendar";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TableSkeleton } from "@/components/loading";

export const Route = createFileRoute("/_authenticated/calendar")({
  component: CalendarPage,
});

// The event's anchor date: exams key off their deadline (end), certificates off issuance (start).
function anchorDate(e: CalendarEvent): Date | null {
  const raw = e.end ?? e.start;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

type Bucket = "Today" | "This Week" | "Later" | "No date";

function bucketOf(d: Date | null): Bucket {
  if (!d) return "No date";
  if (isToday(d)) return "Today";
  if (isThisWeek(d, { weekStartsOn: 1 })) return "This Week";
  return "Later";
}

const BUCKET_ORDER: Bucket[] = ["Today", "This Week", "Later", "No date"];

function typeIcon(type: string) {
  switch (type) {
    case "checkpoint":
      return <ShieldCheck className="h-4 w-4 text-green-500" />;
    case "certificate":
      return <Award className="h-4 w-4 text-violet-500" />;
    default:
      return <CalendarClock className="h-4 w-4 text-amber-500" />;
  }
}

function CalendarPage() {
  const { user } = useAuth();
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["calendar-events", user?.id],
    queryFn: fetchCalendarEvents,
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });

  const grouped: Record<Bucket, CalendarEvent[]> = {
    Today: [],
    "This Week": [],
    Later: [],
    "No date": [],
  };
  for (const e of events) grouped[bucketOf(anchorDate(e))].push(e);
  // Within a bucket, sort by anchor date ascending (undated last).
  for (const b of BUCKET_ORDER) {
    grouped[b].sort((a, c) => {
      const da = anchorDate(a)?.getTime() ?? Infinity;
      const dc = anchorDate(c)?.getTime() ?? Infinity;
      return da - dc;
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-2 text-primary">
          <CalendarDays className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground">
            Your exams, checkpoints, and issued certificates.
          </p>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={5} columns={1} />
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarDays className="mb-3 h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Nothing scheduled yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {BUCKET_ORDER.filter((b) => grouped[b].length > 0).map((bucket) => (
            <div key={bucket} className="space-y-2">
              <h2 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {bucket}
              </h2>
              <div className="space-y-2">
                {grouped[bucket].map((e) => (
                  <EventRow key={`${e.type}-${e.id}`} event={e} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  const d = anchorDate(event);
  const overdue = event.type !== "certificate" && d && isPast(d) && !isToday(d);

  const body = (
    <Card className="transition-colors hover:bg-muted/40">
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="shrink-0">{typeIcon(event.type)}</div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{event.title}</p>
            <p className="truncate text-xs text-muted-foreground">
              {event.course_title ? `${event.course_title} · ` : ""}
              {d ? format(d, "EEE, MMM d, yyyy") : "No date"}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {event.status && (
            <Badge
              variant="outline"
              className={overdue ? "border-destructive/30 text-destructive" : ""}
            >
              {event.status}
            </Badge>
          )}
          {event.link && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );

  return event.link ? (
    <Link to={event.link} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}
